/**
 * Provides shared fail-closed source, origin, decoding, and diagnostic helpers for runtime message boundaries.
 * Callers still select the boundary-specific Valibot schema after decoding succeeds.
 */
import {getAppEnv, getFileServer} from '@qep/shared/src/utils/runtime-config';

const trustedAppSubdomainLabels = [
	'algomanager',
	'audit',
	'blotter',
	'charting',
	'inventory',
	'monitoring',
	'newoptions',
	'options',
	'order-entry',
	'orderbook',
	'orderentry',
	'ordersummary',
	'tickergrid',
] as const;

export type DecodedMessageData =
	| {success: true; output: unknown}
	| {success: false};

export type ParentMessageOriginOptions = {
	appEnv?: string;
	currentOrigin?: string;
	fileServer?: string;
	referrer?: string;
};

/** Resolves an absolute HTTP(S) origin without trusting substring or prefix comparisons. */
export const resolveHttpOrigin = (value: string, baseUrl = globalThis.location?.href): string | undefined => {
	try {
		const url = new URL(value, baseUrl);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') {
			return undefined;
		}

		return url.origin;
	} catch {
		return undefined;
	}
};

/** Resolves only absolute HTTP(S) origins for values used as authorization roots. */
const resolveAbsoluteHttpOrigin = (value: string): string | undefined => {
	try {
		const url = new URL(value);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') {
			return undefined;
		}

		return url.origin;
	} catch {
		return undefined;
	}
};

/**
 * Builds exact Browser and component origins from FILESERVER only when it describes the page's current deployment.
 * Requiring the current page to belong to the derived set prevents URL/query overrides from becoming a trust root.
 */
export const resolveTrustedFrontendOrigins = (fileServer: string, currentOrigin: string): ReadonlySet<string> => {
	const fileServerOrigin = resolveAbsoluteHttpOrigin(fileServer);
	const pageOrigin = resolveAbsoluteHttpOrigin(currentOrigin);
	if (!fileServerOrigin || !pageOrigin) {
		return new Set();
	}

	const fileServerUrl = new URL(fileServerOrigin);
	const origins = new Set([fileServerOrigin]);
	for (const subdomainLabel of trustedAppSubdomainLabels) {
		const componentUrl = new URL(fileServerOrigin);
		componentUrl.hostname = `${subdomainLabel}.app.${fileServerUrl.hostname}`;
		origins.add(componentUrl.origin);
	}

	return origins.has(pageOrigin) ? origins : new Set();
};

/**
 * Resolves the embedding parent only when its referrer matches a configured frontend deployment origin.
 * A same-origin fallback is allowed only for explicitly configured local/development test environments.
 */
export const resolveParentMessageOrigin = ({
	appEnv,
	currentOrigin = globalThis.location?.origin ?? '',
	fileServer = getFileServer(),
	referrer = globalThis.document?.referrer ?? '',
}: ParentMessageOriginOptions = {}): string | undefined => {
	const referrerOrigin = referrer ? resolveAbsoluteHttpOrigin(referrer) : undefined;
	const trustedFrontendOrigins = resolveTrustedFrontendOrigins(fileServer, currentOrigin);
	if (referrerOrigin && trustedFrontendOrigins.has(referrerOrigin)) {
		return referrerOrigin;
	}

	// Authentication test opt-in must not broaden parent-origin trust in deployed UAT.
	const allowsSameOriginFallback = ['local', 'development'].includes(appEnv ?? getAppEnv());
	if (!allowsSameOriginFallback) {
		return undefined;
	}

	return resolveAbsoluteHttpOrigin(currentOrigin);
};

/** Requires a message to come from the exact owned window and one explicitly allowed sender origin. */
export const isTrustedMessageEvent = (
	event: MessageEvent<unknown>,
	expectedSource: MessageEventSource,
	allowedOrigins: ReadonlySet<string>,
): boolean => event.source === expectedSource && allowedOrigins.has(event.origin);

/** Decodes serialized message data once while preserving already-cloned MessagePort/window payloads. */
export const decodeMessageData = (data: unknown): DecodedMessageData => {
	if (typeof data !== 'string') {
		return {success: true, output: data};
	}

	try {
		return {success: true, output: JSON.parse(data) as unknown};
	} catch {
		return {success: false};
	}
};

/** Emits a bounded rejection diagnostic without including event payloads, credentials, or schema details. */
export const logRejectedMessage = (boundary: string, issues: readonly unknown[] = []): void => {
	console.error('Rejected runtime message', {
		boundary,
		issueCount: issues.length,
	});
};
